import { colour, radius, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Page, Select, TextInput, Thumb } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Empty, Loading, OfflineBanner } from '../components/states.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { useMe } from '../app/me.tsx';
import { api, query } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useState } from 'react';

/**
 * Screen 10: home. One card per project, answerable from across the room —
 * is this project in good shape, and is anything waiting on me.
 *
 * Every number comes from the server. A card that made the browser add up
 * 2,500 passes would be slower than the board it links to.
 */

interface ProjectCard {
  project: { project_id: string; name: string; status: string };
  poster: string | null;
  counts: { takes: number; passes: number; good: number; stale: number; rest: number };
  percent: { good: number; stale: number; rest: number };
  needs_a_person: number;
  latest_activity: { at: string; actor: string | null; says: string } | null;
  first_run: { steps: { key: string; says: string; done: boolean }[]; next: string } | null;
}

type Sort = 'activity' | 'name' | 'needs_a_person';

/** The good / stale / rest bar. Each band carries its number, not just a colour. */
function Bar({ percent }: { percent: ProjectCard['percent'] }) {
  const bands = [
    { share: percent.good, fill: colour.good, says: 'good' },
    { share: percent.stale, fill: colour.stale, says: 'stale' },
    { share: percent.rest, fill: colour.cellEmpty, says: 'not made or blocked' }
  ].filter((b) => b.share > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        role="img"
        aria-label={bands.map((b) => `${b.share}% ${b.says}`).join(', ') || 'no VC passes yet'}
        style={{ display: 'flex', height: 10, borderRadius: radius.pill, overflow: 'hidden', background: colour.surfaceSunk }}
      >
        {bands.map((b) => (
          <span key={b.says} style={{ width: `${b.share}%`, background: b.fill }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control }}>
        {bands.map((b) => (
          <span key={b.says} style={{ display: 'flex', alignItems: 'center', gap: 5, ...text.note, color: colour.inkSoft }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: b.fill }} />
            {b.share}% {b.says}
          </span>
        ))}
      </div>
    </div>
  );
}

function FirstRun({ card }: { card: ProjectCard }) {
  const first = card.first_run!;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.tight }}>
      <span style={{ ...text.smallStrong, color: colour.inkSoft }}>
        New project. Next: {first.next}
      </span>
      <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {first.steps.map((step) => (
          <li
            key={step.key}
            style={{
              ...text.small,
              color: step.done ? colour.inkFaint : colour.ink,
              textDecoration: step.done ? 'line-through' : 'none'
            }}
          >
            {step.says}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function Projects({
  onOpen,
  making,
  onCloseMaking,
  onOpenMaking
}: {
  onOpen: (projectId: string) => void;
  /** `/projects/new`, which the New project menu item goes to. */
  making?: boolean;
  onCloseMaking?: () => void;
  onOpenMaking?: () => void;
}) {
  const me = useMe();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('activity');
  const [draft, setDraft] = useState({ project_id: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const canMake = me.status === 'ready' && me.answer.can.new_project;
  // The menu navigates to /projects/new rather than reaching in here, so the
  // dialog follows the route: the link can be shared, and Back closes it.
  const open = making === true && canMake;

  const close = (): void => {
    setDraft({ project_id: '', name: '' });
    onCloseMaking?.();
  };

  const create = async (): Promise<void> => {
    setSaving(true);
    try {
      const made = await api.post<{ project: { project_id: string } }>('/api/projects', {
        project_id: draft.project_id.trim(),
        name: draft.name.trim()
      });
      close();
      // Straight into it: the next thing anybody does with a new project is
      // look at its board.
      onOpen(made.project.project_id);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That project could not be created.');
    } finally {
      setSaving(false);
    }
  };

  // The code is what every other screen's URL is built from, so it is checked
  // here in the same words the server uses rather than left to a 400.
  const codeProblem =
    draft.project_id.trim() === ''
      ? 'A project needs a short code.'
      : !/^[A-Za-z0-9_-]+$/.test(draft.project_id.trim())
        ? 'Letters, numbers, dashes and underscores only — it goes in every URL.'
        : null;
  const ready = codeProblem === null && draft.name.trim() !== '';

  const { state, reload } = useLoad<{ projects: ProjectCard[] }>(
    () => api.get(`/api/projects/home${query({ search, sort })}`),
    [search, sort]
  );

  const cards = state.data?.projects ?? [];

  return (
    <Page
      eyebrow="MAESTRO · PROJECTS"
      title="Projects"
      description="What is the latest known good, and where is it? Open a project to find out."
    >
      {state.status === 'offline' && <OfflineBanner since={state.since} onRetry={reload} />}

      <Card>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: space.control }}>
          <Field label="Search by name or code" grow>
            <TextInput value={search} onChange={setSearch} placeholder="Sample, SAMPLE…" />
          </Field>
          <Field label="Sort by">
            <Select<Sort>
              value={sort}
              onChange={setSort}
              options={[
                { value: 'activity', label: 'Latest activity' },
                { value: 'name', label: 'Name' },
                { value: 'needs_a_person', label: 'Needs a person' }
              ]}
            />
          </Field>
          {/* Also in the project menu. It is here too because this is the
              page somebody is on when they want one. */}
          {canMake && (
            <Button tone="primary" onClick={() => onOpenMaking?.()}>
              New project
            </Button>
          )}
        </div>
      </Card>

      {state.status === 'loading' && cards.length === 0 && <Loading says="Loading projects…" rows={3} />}
      {state.status === 'failed' && cards.length === 0 && (
        <CouldNotLoad what="your projects" error={state.error} onRetry={reload} />
      )}
      {state.status !== 'loading' && cards.length === 0 && state.status !== 'failed' && (
        <Empty
          title={search ? 'No project matches that' : 'No projects yet'}
          says={
            search
              ? 'Try a shorter search, or clear it to see everything.'
              : 'An admin makes the first one. It starts with a copy of the default pipeline.'
          }
          {...(search
            ? { action: <Button onClick={() => setSearch('')}>Clear the search</Button> }
            : canMake
              ? {
                  action: (
                    <Button tone="primary" onClick={() => onOpenMaking?.()}>
                      New project
                    </Button>
                  )
                }
              : {})}
        />
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))',
          gap: space.control
        }}
      >
        {cards.map((card) => (
          <Card key={card.project.project_id}>
            <div style={{ display: 'flex', gap: space.control, alignItems: 'flex-start' }}>
              <Thumb url={card.poster} alt={`${card.project.name} poster`} size={96} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                <span style={{ ...text.label, color: colour.inkMuted }}>{card.project.project_id}</span>
                <h2 style={{ margin: 0, ...text.h3 }}>{card.project.name}</h2>
                {card.latest_activity ? (
                  <span style={{ ...text.note, color: colour.inkSoft }}>
                    Last: {card.latest_activity.says}
                    {card.latest_activity.actor ? `, by ${card.latest_activity.actor}` : ''} ·{' '}
                    {new Date(card.latest_activity.at).toLocaleDateString()}
                  </span>
                ) : (
                  <span style={{ ...text.note, color: colour.inkFaint }}>Nothing has happened yet</span>
                )}
              </div>
            </div>

            {card.first_run ? (
              <FirstRun card={card} />
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.card }}>
                  <span style={{ ...text.small, color: colour.inkSoft }}>
                    <strong style={{ font: text.bodyStrong.font }}>{card.counts.takes}</strong> takes
                  </span>
                  <span style={{ ...text.small, color: colour.inkSoft }}>
                    <strong style={{ font: text.bodyStrong.font }}>{card.counts.passes}</strong> VC passes
                  </span>
                  <span style={{ ...text.small, color: colour.inkSoft }}>
                    <strong style={{ font: text.bodyStrong.font }}>{card.percent.good}%</strong> good movie
                  </span>
                </div>
                <Bar percent={card.percent} />
              </>
            )}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: space.tight,
                paddingTop: space.tight,
                borderTop: `1px solid ${colour.edgeSoft}`
              }}
            >
              {card.needs_a_person > 0 ? (
                <span style={{ ...text.smallStrong, color: colour.staleInk }}>
                  {card.needs_a_person} {card.needs_a_person === 1 ? 'thing needs' : 'things need'} a person
                </span>
              ) : (
                <span style={{ ...text.small, color: colour.inkMuted }}>Nothing is waiting on anybody</span>
              )}
              <Button tone="primary" onClick={() => onOpen(card.project.project_id)}>
                Open
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {open && (
        <Dialog
          title="New project"
          says="The code is what every screen's address is built from, so it is worth keeping short. The project starts with its own copy of the default pipeline, and changing its steps later touches nobody else."
          confirm="Create it"
          confirmWhyNot={
            codeProblem ?? (draft.name.trim() === '' ? 'Give it a name people will read.' : undefined)
          }
          onConfirm={() => {
            if (ready && !saving) void create();
          }}
          onClose={close}
          busy={saving}
        >
          <Field label="Short code">
            <TextInput
              value={draft.project_id}
              onChange={(project_id) => setDraft({ ...draft, project_id })}
              placeholder="GHOST24"
            />
          </Field>
          {draft.project_id !== '' && codeProblem && (
            <span style={{ ...text.note, color: colour.staleInk }}>{codeProblem}</span>
          )}

          <Field label="What people call it">
            <TextInput
              value={draft.name}
              onChange={(name) => setDraft({ ...draft, name })}
              placeholder="Ghost, season 2"
            />
          </Field>

          {ready && (
            <span style={{ ...text.note, color: colour.inkMuted }}>
              Its board will be at /p/{draft.project_id.trim()}/board
            </span>
          )}
        </Dialog>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
